import { action, computed } from 'mobx'
import type { FetchTeamMemberFn, GetCurrentMemberFn } from '@/features/iam'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { type TeamMember } from '@/features/iam/teams/TeamMember'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { memoizeAsync } from '@/utils/memoize-decorator'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { CollaboratorViewModel } from '@/features/jobs/details/components/CollaboratorsCard/CollaboratorViewModel'
import { type RatingsViewModel } from '@/features/jobs/details/ratings/RatingsViewModel'
import { type AddCollaboratorsAsAdminDialogViewModel } from '@/features/jobs/details/components/AddCollaboratorsAsAdminDialog/AddCollaboratorsAsAdminDialogViewModel'
import { type RemoveCollaboratorsAsAdminDialogViewModel } from '@/features/jobs/details/components/RemoveCollaboratorsAsAdminDialog/RemoveCollaboratorsAsAdminDialogViewModel'
import { type AddClientsAsTeamMemberDialogViewModel } from '@/features/jobs/details/components/AddClientsAsTeamMemberDialog/AddClientsAsTeamMemberDialogViewModel'
import { type RemoveClientsAsNonAdminDialogViewModel } from '@/features/jobs/details/components/RemoveClientsAsNonAdminDialog/RemoveClientsAsNonAdminDialogViewModel'
import { type RemoveClientTeamDialogViewModel } from '@/features/jobs/details/components/RemoveClientTeamDialog/RemoveClientTeamDialogViewModel'
import { type TransferJobDialogViewModel } from '@/features/jobs/TransferJobDialog/TransferJobDialogViewModel'

/**
 * The people on a job, and what the current user can change about them.
 */
export class JobCollaboratorsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _collaborators = new ViewModelList({
    models: () => this._job.collaborators.slice(),
    create: (collaborator) =>
      new CollaboratorViewModel(collaborator, this._getCurrentMember),
  })

  constructor(
    private readonly _job: Job,
    private readonly _getCurrentTeamMemberOfOwnerTeam: () =>
      TeamMember | undefined,
    private readonly _jobStore: JobStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _fetchTeamMember: FetchTeamMemberFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    public readonly ratingsVm: RatingsViewModel,
    public readonly addCollaboratorsAsAdminVm: AddCollaboratorsAsAdminDialogViewModel,
    public readonly removeCollaboratorsAsAdminVm: RemoveCollaboratorsAsAdminDialogViewModel,
    public readonly addClientsAsTeamMemberVm: AddClientsAsTeamMemberDialogViewModel,
    public readonly removeClientsAsNonAdminVm: RemoveClientsAsNonAdminDialogViewModel,
    public readonly removeClientTeamVm: RemoveClientTeamDialogViewModel,
    public readonly transferJobVm: TransferJobDialogViewModel,
  ) {
    this._lifecycle.manage(this._collaborators).manage(ratingsVm)
  }

  /**
   * All collaborators, including you.
   */
  get collaborators() {
    return this._collaborators.items
  }

  /**
   * Collaborator for the current user.
   */
  @computed
  get currentUserCollaborator() {
    return this.collaborators.find((c) => c.isCurrentUser)
  }

  /**
   * Champion pro collaborator
   */
  @computed
  get championPro() {
    return this.collaborators.find(
      (c) => c.role === 'Champion' && c.type === 'Pro',
    )
  }

  /**
   * Champion client collaborator
   */
  @computed
  get championClient() {
    return this.collaborators.find(
      (c) => c.role === 'Champion' && c.type === 'Client',
    )
  }

  /**
   * Supporting Pros on the Job.
   */
  @computed
  get supportingPros() {
    return this._job.supportingPros
  }

  /**
   * Supporting Clients on the Job.
   */
  @computed
  get supportingClients() {
    return this._job.supportingClients
  }

  /**
   * Participant member to display in the chat's conversation header.
   */
  @computed
  get participantMemberToDisplay() {
    // Show champion pro participant if we are the client, otherwise show the client
    return this.currentUserCollaborator?.type === 'Client'
      ? this.championPro?.collaborator.member
      : this.championClient?.collaborator.member
  }

  /**
   * Whether the scheduled provider should be shown in the collaborators section.
   */
  @computed
  get showingRequestedProvider() {
    return (
      this._job.requestedPro &&
      !this._job.proChampion &&
      (this._job.status === 'UnderConstruction' ||
        this._job.status === 'InfoGathering' ||
        this._job.status === 'Unclaimed')
    )
  }

  @computed
  get canAddCollaboratorsAsAdmin() {
    return this.canAddProvidersAsAdmin || this.canAddClientsAsAdmin
  }

  /**
   * Determines whether we can add Providers on the job as Admin.
   */
  @computed
  get canAddProvidersAsAdmin() {
    return (
      this.canAddChampionProviderAsAdmin || this.canAddSupportingProviderAsAdmin
    )
  }

  /**
   * Determines whether we can add a champion Pro on the job as Admin.
   */
  @computed
  get canAddChampionProviderAsAdmin() {
    const permissions = this._permissions
    return (
      !this._job.completed &&
      (this._job.status === 'Unclaimed' ||
        (this._job.status === 'InfoGathering' &&
          this._job.consultation !== null)) &&
      permissions.adminJobChampionProviderManage &&
      permissions.adminUserList &&
      permissions.adminUserRead &&
      permissions.adminJobView
    )
  }

  /**
   * Determines whether we can add a supporting Pro on the job as Admin.
   */
  @computed
  get canAddSupportingProviderAsAdmin() {
    const permissions = this._permissions
    return (
      !this._job.completed &&
      (this._job.status === 'Claimed' ||
        this._job.status === 'OnHold' ||
        this._job.status === 'Idle') &&
      permissions.adminJobChampionProviderManage &&
      permissions.adminJobSupportingProviderManage &&
      permissions.adminUserList &&
      permissions.adminUserRead &&
      permissions.adminJobView
    )
  }

  /**
   * Determines whether we can add Clients on the job as Admin.
   */
  @computed
  get canAddClientsAsAdmin() {
    return this._canManageSupportingClientsAsAdmin
  }

  /**
   * Whether a non-admin can add clients, offered only when the admin action
   * isn't.
   */
  @computed
  get canAddClientsAsNonAdmin() {
    return !this.canAddClientsAsAdmin && this.canAddClientsAsTeamMember
  }

  /**
   * Determines whether we can remove pros as Admin.
   */
  @computed
  get canRemoveProsAsAdmin() {
    return (
      this.supportingPros.length > 0 && this._canRemoveSupportingProsAsAdmin
    )
  }

  /**
   * Determines whether we can remove clients as Admin.
   */
  @computed
  get canRemoveClientsAsAdmin() {
    return (
      this.supportingClients.length > 0 &&
      this._canManageSupportingClientsAsAdmin
    )
  }

  /**
   * Determines whether we can remove collaborators as Admin.
   */
  @computed
  get canRemoveCollaboratorsAsAdmin() {
    return (
      (this.supportingClients.length > 0 || this.supportingPros.length > 0) &&
      (this._canManageSupportingClientsAsAdmin ||
        this._canRemoveSupportingProsAsAdmin)
    )
  }

  /**
   * Determines whether we can add clients as a team member.
   */
  @computed
  get canAddClientsAsTeamMember() {
    return Boolean(
      this._getCurrentTeamMemberOfOwnerTeam()?.permissions.updateTeamJobs,
    )
  }

  /**
   * Determines whether we can remove clients as a team member.
   */
  @computed
  get canRemoveClientsAsTeamMember() {
    if (this._job.supportingClients.length === 0) {
      return false
    }

    return Boolean(
      this._getCurrentTeamMemberOfOwnerTeam()?.permissions.updateTeamJobs,
    )
  }

  /**
   * Determines whether we can remove clients as a client.
   */
  @computed
  get canRemoveClientsAsCollaborator() {
    return this._job.canRemoveClientsAsCollaborator
  }

  /**
   * Determines whether we can remove clients as a non-admin.
   */
  @computed
  get canRemoveClientsAsNonAdmin() {
    return (
      this.canRemoveClientsAsTeamMember || this.canRemoveClientsAsCollaborator
    )
  }

  /**
   * Whether a non-admin can remove clients, offered only when the admin
   * action isn't.
   */
  @computed
  get canRemoveClientsAsNonAdminOnly() {
    return !this.canRemoveClientsAsAdmin && this.canRemoveClientsAsNonAdmin
  }

  /**
   * Determines whether we can view the collaborators action menu
   */
  @computed
  get canViewCollaboratorActionsMenu() {
    return (
      this.canAddCollaboratorsAsAdmin ||
      this.ratingsVm.canRatePro ||
      this.canRemoveCollaboratorsAsAdmin ||
      this.canAddClientsAsTeamMember ||
      this.canRemoveClientsAsTeamMember ||
      this.canRemoveClientsAsCollaborator
    )
  }

  /**
   * Determines whether we can view the requested provider action menu
   */
  @computed
  get canViewRequestedProviderActionsMenu() {
    return this.canRequestChampionProviderAsAdmin
  }

  /**
   * Determines whether we can request a champion Pro on the job as Admin.
   */
  @computed
  get canRequestChampionProviderAsAdmin() {
    return (
      this._job.status === 'InfoGathering' &&
      this._permissions.adminJobChampionProviderManage
    )
  }

  /**
   * Whether we are able to remove the client team from the job as an admin.
   */
  @computed
  get canRemoveClientTeamAsAdmin() {
    if (!this._job.ownerTeam?.id) {
      return false
    }

    if (!this._job.teams.some((x) => x.id === this._job.ownerTeam?.id)) {
      return false
    }

    return (
      this._permissions.adminManageTeamMember &&
      this._permissions.adminAssignTeam
    )
  }

  /**
   * Whether we hold the admin permissions to transfer the job, whatever its
   * consultation.
   */
  @computed
  get canTransferAsAdmin() {
    const permissions = this._permissions
    return (
      !this._job.completed &&
      permissions.adminJobTransfer &&
      permissions.adminUserList &&
      permissions.adminUserRead &&
      permissions.adminJobView
    )
  }

  /**
   * Whether we are able to make any transfer in a job.
   */
  @computed
  get canTransferJobAsAdmin() {
    return this._job.hasActiveConsultation
      ? (this._job.isClaimedJob || this._job.isOnHold) &&
          this.canTransferAsAdmin
      : this.canTransferAsAdmin
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Opens the transfer dialog for this job.
   */
  @action.bound
  transferJob() {
    this.transferJobVm.open([this._job])
  }

  /**
   * Removes the requested champion provider.
   */
  @action.bound
  async removeRequestedChampionProviderAsAdmin() {
    await this._jobStore
      .requestChampionProviderAsAdmin(this._job.id, null)
      .catch(this._createAlertErrorHandler('Removing the requested Pro failed'))
  }

  /**
   * If the current user is representing a team on the job, fetches their
   * team member.
   */
  @memoizeAsync()
  async fetchTeamMemberForCurrentUser(signal?: AbortSignal) {
    const collaborator = this._job.collaborators.find(
      (c) => c.member.isCurrentUser,
    )
    if (collaborator?.team) {
      await this._fetchTeamMember(
        collaborator.team.id,
        collaborator.member.user.publicId,
        signal,
      )
    }
  }

  /**
   * Adding and removing supporting Clients as an admin take the same
   * permissions.
   */
  @computed
  private get _canManageSupportingClientsAsAdmin() {
    const permissions = this._permissions
    return (
      !this._job.completed &&
      permissions.adminJobClientsManage &&
      (permissions.adminUserList || permissions.adminClientUserList)
    )
  }

  @computed
  private get _canRemoveSupportingProsAsAdmin() {
    return (
      !this._job.completed &&
      this._permissions.adminJobSupportingProviderManage &&
      this._permissions.adminUserList
    )
  }

  @computed
  private get _permissions() {
    return this._getCurrentMember(this._job.workspace.id).permissions
  }
}
